import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import AppDatatable from '../../components/AppDatatable';
import AccessDeniedState from '../../components/AccessDeniedState';
import { createLocalId, deleteLocalItem, normalizeStatus, readLocalCollection, upsertLocalItem } from '../../utils/localData';
import { getLoggedInUser, hasModulePermission } from '../../utils/permissions';

const STORAGE_KEY = 'ra_locations_local';
const LOCATION_PERMISSION_KEYS = ['Location', 'Location Setup'];
const DEFAULT_LOCATION_COLOR = '#dbeafe';

const createInitialForm = () => ({
  _id: '',
  location_name: '',
  code: '',
  address: '',
  email: '',
  phone_number: '',
  point_of_contact_email: '',
  notification_emails: '',
  fein_tax_number: '',
  claim_row_color: DEFAULT_LOCATION_COLOR,
  status: 'Active',
});

const formGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: 2,
  mt: 0.5,
  alignItems: 'start',
};

export default function LocationPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(createInitialForm());
  const [error, setError] = useState('');
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const currentUser = getLoggedInUser();
  const currentUserName = currentUser?.userName || '';
  const accessRole = currentUser?.accessRole || {};
  const canView = hasModulePermission(LOCATION_PERMISSION_KEYS, 'View', accessRole);
  const canCreate = hasModulePermission(LOCATION_PERMISSION_KEYS, 'Create', accessRole);
  const canEdit = hasModulePermission(LOCATION_PERMISSION_KEYS, 'Edit', accessRole);
  const canDelete = hasModulePermission(LOCATION_PERMISSION_KEYS, 'Delete', accessRole);

  const fetchLocations = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${baseURL}/get_locations`);
      const apiRows = Array.isArray(response?.data) ? response.data : [];
      const localRows = readLocalCollection(STORAGE_KEY);
      const merged = [...apiRows];

      localRows.forEach((localRow) => {
        if (!merged.some((apiRow) => String(apiRow?._id || apiRow?.location_name) === String(localRow?._id || localRow?.location_name))) {
          merged.push(localRow);
        }
      });

      setRows(merged);
    } catch {
      setRows(readLocalCollection(STORAGE_KEY));
      setError('Locations API is not available, so local data is being shown.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleChange = ({ target: { name, value } }) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(createInitialForm());
    setOpenDialog(false);
    setSubmitting(false);
  };

  const openCreate = () => {
    if (!canCreate) return;
    setForm(createInitialForm());
    setOpenDialog(true);
  };

  const openEdit = (row) => {
    if (!canEdit) return;
    setForm({
      _id: row?._id || '',
      location_name: row?.location_name || '',
      code: row?.code || '',
      address: row?.address || '',
      email: row?.email || row?.contact || '',
      phone_number: row?.phone_number || row?.phone || '',
      point_of_contact_email: row?.point_of_contact_email || row?.contact_email || '',
      notification_emails: Array.isArray(row?.notification_emails) ? row.notification_emails.join(', ') : (row?.notification_emails || ''),
      fein_tax_number: row?.fein_tax_number || row?.tax_number || row?.fein || '',
      claim_row_color: row?.claim_row_color || DEFAULT_LOCATION_COLOR,
      status: row?.status || 'Active',
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if ((!form._id && !canCreate) || (form._id && !canEdit)) {
      await Swal.fire({
        icon: 'warning',
        text: form._id ? 'You do not have permission to edit locations.' : 'You do not have permission to create locations.',
      });
      return;
    }

    if (!form.location_name.trim() || !form.code.trim() || !form.claim_row_color) {
      Swal.fire({ icon: 'warning', text: 'Location name, code, and claim color are required.' });
      return;
    }

    if (!/^#[0-9a-f]{6}$/i.test(form.claim_row_color.trim())) {
      Swal.fire({ icon: 'warning', text: 'Choose a valid six-digit color.' });
      return;
    }

    const notificationEmails = form.notification_emails.split(/[,;\n]+/).map((email) => email.trim().toLowerCase()).filter(Boolean);
    if (notificationEmails.some((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      Swal.fire({ icon: 'warning', text: 'Enter valid notification email addresses separated by commas.' });
      return;
    }

    const duplicateColor = rows.some((row) => (
      String(row._id) !== String(form._id)
      && String(row.claim_row_color || '').trim().toLowerCase() === form.claim_row_color.trim().toLowerCase()
    ));
    if (duplicateColor) {
      Swal.fire({ icon: 'warning', text: 'This claim color is already assigned to another location. Please choose a unique color.' });
      return;
    }

    const payload = {
      ...form,
      notification_emails: [...new Set(notificationEmails)],
      _id: form._id || createLocalId('location'),
      ...(form._id ? { updated_by: currentUserName } : { created_by: currentUserName }),
    };

    setSubmitting(true);
    try {
      let response;

      if (form._id) {
        response = await axios.put(`${baseURL}/update_location/${form._id}`, payload);
      } else {
        response = await axios.post(`${baseURL}/create_location`, payload);
      }

      const result = response?.data || {};

      if (result.status_code === 1) {
        await Swal.fire({
          icon: 'success',
          text: result.message || (form._id ? 'Location updated successfully' : 'Location created successfully'),
        });
        await fetchLocations();
        resetForm();
        return;
      }

      await Swal.fire({
        icon: 'error',
        text: result.message || 'Unable to save location.',
      });
    } catch {
      upsertLocalItem(STORAGE_KEY, payload);
      await fetchLocations();
      await Swal.fire({
        icon: 'success',
        text: form._id ? 'Location updated locally.' : 'Location created locally.',
      });
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!canDelete) {
      await Swal.fire({
        icon: 'warning',
        text: 'You do not have permission to delete locations.',
      });
      return;
    }

    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Delete location?',
      text: `Remove ${row.location_name}?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#c62828',
    });

    if (!confirmation.isConfirmed) return;

    try {
      const response = await axios.delete(`${baseURL}/delete_location/${row._id}`, {
        data: { deleted_by: currentUserName },
      });

      const result = response?.data || {};

      if (result.status_code === 1) {
        await Swal.fire({
          icon: 'success',
          text: result.message || 'Location deleted successfully',
        });
        await fetchLocations();
        return;
      }

      await Swal.fire({
        icon: 'error',
        text: result.message || 'Unable to delete location.',
      });
    } catch {
      deleteLocalItem(STORAGE_KEY, row._id);
      await fetchLocations();
      await Swal.fire({
        icon: 'success',
        text: 'Location deleted locally.',
      });
    }
  };

  const tableData = useMemo(() => ({
    columns: [
      { label: 'Location Name', field: 'location_name' },
      { label: 'Code', field: 'code' },
      { label: 'Address', field: 'address' },
      { label: 'Contact Details', field: 'email' },
      { label: 'Phone Number', field: 'phone_number' },
      { label: 'POC Email', field: 'point_of_contact_email' },
      { label: 'Notification Emails', field: 'notification_emails' },
      { label: 'FEIN/TAX Number', field: 'fein_tax_number' },
      { label: 'Claim Color', field: 'claim_row_color' },
      { label: 'Status', field: 'status' },
      { label: 'Action', field: 'action', sort: false },
    ],
    rows: rows.map((row) => ({
      location_name: row.location_name || '-',
      code: row.code || '-',
      address: row.address || '-',
      email: row.email || row.contact || '-',
      phone_number: row.phone_number || row.phone || '-',
      point_of_contact_email: row.point_of_contact_email || row.contact_email || '-',
      notification_emails: Array.isArray(row.notification_emails) ? row.notification_emails.join(', ') : (row.notification_emails || '-'),
      fein_tax_number: row.fein_tax_number || row.tax_number || row.fein || '-',
      claim_row_color: (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: row.claim_row_color || '#ffffff', border: '1px solid #94a3b8' }} />
          <span>{row.claim_row_color || 'Not assigned'}</span>
        </Stack>
      ),
      status: normalizeStatus((row.status || 'Active') === 'Active'),
      action: (
        <Stack direction="row" spacing={1}>
          {canEdit ? (
            <Button size="small" variant="outlined" onClick={() => openEdit(row)}>
              Edit
            </Button>
          ) : null}
          {canDelete ? (
            <Button size="small" color="error" variant="contained" onClick={() => handleDelete(row)}>
              Delete
            </Button>
          ) : null}
          {!canEdit && !canDelete ? '-' : null}
        </Stack>
      ),
    })),
  }), [canDelete, canEdit, rows]);

  if (!canView) {
    return <AccessDeniedState message="You do not have permission to view locations." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', ml: { md: '240px' }, mt: '64px', p: { xs: 2, md: 4 } }}>
      <AppDatatable
        title="Locations"
        subtitle="Search, paginate, and manage created locations here."
        headerActions={canCreate ? <Button variant="contained" onClick={openCreate}>Create Location</Button> : null}
        headerContent={error ? <Typography sx={{ color: 'warning.main' }}>{error}</Typography> : null}
        data={tableData}
        loading={loading}
        noFoundMessage="No locations created yet."
      />

      <Dialog open={openDialog} onClose={resetForm} fullWidth maxWidth="md">
        <DialogTitle>{form._id ? 'Edit Location' : 'Create Location'}</DialogTitle>
        <DialogContent>
          <Box sx={formGridSx}>
            <TextField
              label="Location Name"
              name="location_name"
              value={form.location_name}
              onChange={handleChange}
              required
            />
            <TextField
              label="Code"
              name="code"
              value={form.code}
              onChange={handleChange}
              required
            />
            <TextField
              label="Email ID"
              name="email"
              value={form.email}
              onChange={handleChange}
            />
            <TextField
              label="Phone Number"
              name="phone_number"
              value={form.phone_number}
              onChange={handleChange}
            />
            <TextField
              label="Point of Contact Email"
              name="point_of_contact_email"
              value={form.point_of_contact_email}
              onChange={handleChange}
            />
            <TextField
              label="Notification Emails"
              name="notification_emails"
              value={form.notification_emails}
              onChange={handleChange}
              placeholder="claims@example.com, manager@example.com"
              helperText="Separate multiple email addresses with commas."
              multiline
              minRows={2}
            />
            <TextField
              label="FEIN/TAX Number"
              name="fein_tax_number"
              value={form.fein_tax_number}
              onChange={handleChange}
            />
            <TextField
              label="Address"
              name="address"
              value={form.address}
              onChange={handleChange}
              multiline
              minRows={3}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>Claim Row Color *</Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <input
                  type="color"
                  aria-label="Claim row color"
                  name="claim_row_color"
                  value={form.claim_row_color}
                  onChange={handleChange}
                  style={{ width: 58, height: 42, border: 0, background: 'transparent', cursor: 'pointer' }}
                />
                <TextField
                  size="small"
                  name="claim_row_color"
                  value={form.claim_row_color}
                  onChange={handleChange}
                  inputProps={{ pattern: '^#[0-9A-Fa-f]{6}$' }}
                />
              </Stack>
            </Box>
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" name="status" value={form.status} onChange={handleChange}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={resetForm}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
